import styled from "@emotion/styled";
import { useCallback, useState, type MouseEventHandler } from "react";

import { colors } from "@skilitics/designer-theme";
import { Icon, IconButton, type IconName } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { createTagsFilter } from "../filters";
import { useLandingPageSettings } from "../LandingPageSettingsContext";
import { FilterWizard, useFilterSettings, type FilterType } from "../wizards";

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  font-size: 14px;

  border: 1px solid ${colors.uiGrey["300"]};
  border-radius: 50px;

  padding: 0 2px 0 12px;

  cursor: pointer;
`;

const DeleteButton = styled(IconButton)`
  height: 24px;

  color: ${colors.skiliticsRed["600"]};

  margin-inline-start: -4px;
`;

type Props = {
  id: FilterType;
  icon: IconName;
};

export const FilterSet = ({ id, icon }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const [open, setOpen] = useState(false);

  const { addFilterSet, getFilterSet, removeFilterSet } = useLandingPageSettings();

  const { setType, tags, setTags, reset } = useFilterSettings();

  const handleSetup = useCallback(() => {
    setType("tags");

    const existingTags = (getFilterSet("tags")?.metadata ?? []) as string[];
    setTags(existingTags);

    setOpen(true);
  }, [getFilterSet, setTags, setType]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleBegin = useCallback(() => id, [id]);

  const handleRemove: MouseEventHandler = useCallback(
    (event) => {
      event.stopPropagation();
      removeFilterSet(id);
      reset();
    },
    [id, removeFilterSet, reset],
  );

  const handleComplete = useCallback(async () => {
    if (tags.length) {
      addFilterSet({ id: "tags", filters: [createTagsFilter(tags)], operator: "and", metadata: tags });
    }

    setOpen(false);
    reset();
  }, [addFilterSet, reset, tags]);

  return (
    <>
      <Container onClick={handleSetup} data-test="filter-container">
        <Icon icon={icon} size={14} />
        {formatMessage(id)}
        <DeleteButton icon="xmark" onClick={handleRemove} data-test="remove-filter-button" />
      </Container>
      <FilterWizard
        open={open}
        onBegin={handleBegin}
        onCancel={handleClose}
        onClose={handleClose}
        onComplete={handleComplete}
        contentStyle={{ height: "380px" }}
        stepperStyle={{ padding: "0 45px 52px 45px" }}
      />
    </>
  );
};

const formatStrings = {
  date: {
    en: "Date",
  },
  tags: {
    en: "Tags",
  },
} satisfies FormatMessages;
